import { useEffect, useState } from "react";
import { AlertTriangle, IndianRupee, ShoppingBag, TrendingUp, Wallet } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import api from "@/lib/api";
import { inr } from "@/components/admin/orderUtils";

const Stat = ({ label, value, sub, icon: Icon, testId }) => (
  <div className="bg-white rounded-3xl border border-warm p-4 sm:p-5" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-[0.15em] font-bold text-charcoal/50">{label}</p>
      <div className="w-8 h-8 rounded-xl rose-glow flex items-center justify-center text-white"><Icon className="w-4 h-4" /></div>
    </div>
    <p className="font-display text-2xl sm:text-3xl text-charcoal mt-3" data-testid={`${testId}-value`}>{value}</p>
    {sub && <p className="text-xs text-charcoal/50 mt-1">{sub}</p>}
  </div>
);

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-IN", { month: "short", year: "2-digit" });

export const ProfitDashboard = () => {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/profit-summary").then(({ data }) => setD(data)).catch(() => setErr(true)); }, []);
  if (err) return <p className="text-center py-16 text-charcoal/60" data-testid="profit-load-error">Could not load the profit dashboard. Please refresh.</p>;
  if (!d) return <div className="h-64 rounded-3xl bg-white/60 border border-warm animate-pulse" />;

  const maxOcc = Math.max(1, ...d.by_occasion.map((o) => o.revenue));
  return (
    <div className="space-y-6" data-testid="profit-dashboard">
      {d.items_missing_cost > 0 && (
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 flex gap-2" data-testid="profit-missing-cost-warning">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          {d.items_missing_cost} ordered item(s) have no cost price yet, so profit counts them as zero cost. Add costs in the Costs tab.
        </div>
      )}
      <p className="text-xs text-charcoal/50">Cancelled orders are excluded. Profit = order total (after discounts, incl. delivery fees) − item cost prices. Weeks and months follow IST.</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Stat label="Revenue · all time" value={inr(d.revenue.all_time)} sub={`Profit ${inr(d.profit.all_time)}`} icon={IndianRupee} testId="profit-revenue-all" />
        <Stat label="This month" value={inr(d.revenue.this_month)} sub={`Profit ${inr(d.profit.this_month)}`} icon={TrendingUp} testId="profit-revenue-month" />
        <Stat label="This week" value={inr(d.revenue.this_week)} sub={`Profit ${inr(d.profit.this_week)}`} icon={Wallet} testId="profit-revenue-week" />
        <Stat label="Orders" value={d.total_orders} sub={`Avg order ${inr(d.average_order_value)}`} icon={ShoppingBag} testId="profit-total-orders" />
      </div>

      <div className="bg-white rounded-3xl border border-warm p-4 sm:p-6" data-testid="profit-monthly-chart">
        <h3 className="font-display text-xl text-charcoal mb-4">Revenue trend · last 12 months</h3>
        <div className="h-64 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={d.monthly_trend.map((m) => ({ ...m, label: monthLabel(m.month) }))} margin={{ left: -10, right: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1e4e8" vertical={false} />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v)} />
              <Tooltip formatter={(v) => inr(v)} cursor={{ fill: "#fdf2f8" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="revenue" name="Revenue" fill="#E11D48" radius={[6, 6, 0, 0]} />
              <Bar dataKey="profit" name="Profit" fill="#9333EA" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-3xl border border-warm p-4 sm:p-6" data-testid="profit-by-occasion">
          <h3 className="font-display text-xl text-charcoal mb-4">Revenue by occasion</h3>
          <div className="space-y-3">
            {d.by_occasion.map((o) => (
              <div key={o.occasion} data-testid={`profit-occasion-${o.occasion.toLowerCase()}`}>
                <div className="flex justify-between text-sm mb-1"><span className="font-semibold text-charcoal">{o.occasion}</span><span className="text-charcoal/70">{inr(o.revenue)} · {o.orders} orders</span></div>
                <div className="h-2 rounded-full bg-rose-50 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-rose-500 to-violet-500" style={{ width: `${(o.revenue / maxOcc) * 100}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-3xl border border-warm p-4 sm:p-6" data-testid="profit-top-items">
          <h3 className="font-display text-xl text-charcoal mb-4">Top 5 most booked</h3>
          <ol className="space-y-3">
            {d.top_items.map((t, i) => (
              <li key={t.name} className="flex items-center gap-3" data-testid={`profit-top-item-${i}`}>
                <span className="w-7 h-7 rounded-full rose-glow text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                <div className="min-w-0 flex-1"><p className="text-sm font-semibold text-charcoal truncate">{t.name}</p><p className="text-xs text-charcoal/50 capitalize">{t.kind}</p></div>
                <div className="text-right text-sm"><p className="font-semibold">{t.quantity} booked</p><p className="text-xs text-charcoal/50">{inr(t.revenue)}</p></div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </div>
  );
};
